import { useCallback, useMemo, useRef, useState } from 'react';
import { useGoogleAnalyticsPageView } from './analytics/googleAnalytics';
import { learningApi } from './api/learning';
import { MobileNav } from './components/layout/MobileNav';
import { Sidebar } from './components/layout/Sidebar';
import type { ViewName } from './components/layout/navigation';
import { TodayView } from './features/dashboard/TodayView';
import { LandingPage } from './features/landing/LandingPage';
import { PrivacyPage } from './features/landing/PrivacyPage';
import { AnalyticsConsent } from './features/privacy/AnalyticsConsent';
import { ExerciseSession } from './features/exercises/ExerciseSession';
import { buildProgressModel } from './features/progress/progressModel';
import { ProgressView } from './features/progress/ProgressView';
import { VocabularyView } from './features/vocabulary/VocabularyView';
import { VerbPhraseView } from './features/verb-phrases/VerbPhraseView';
import { useAuth } from './hooks/useAuth';
import { useProgress } from './hooks/useProgress';
import { useVocabularyTraining } from './hooks/useVocabularyTraining';
import { useVerbPhraseCourse } from './hooks/useVerbPhraseCourse';
import { LoadingScreen } from './shared/LoadingScreen';
import { Toast } from './shared/Toast';
import type { Exercise } from './types/content';

export default function App() {
  const auth = useAuth();
  const privacyPage = window.location.pathname === '/personvern';
  const { progress, error: progressError, saveAttempt } = useProgress(auth.status === 'authenticated', auth.logout);
  const vocabularyTraining = useVocabularyTraining(auth.status === 'authenticated');
  const verbPhraseEnabled = Boolean(auth.user?.features.includes('verb-phrases'));
  const verbPhraseCourse = useVerbPhraseCourse(auth.status === 'authenticated' && verbPhraseEnabled);
  const [view, setView] = useState<ViewName>('today');
  const [queue, setQueue] = useState<Exercise[]>([]);
  const [sessionKind, setSessionKind] = useState<'vocabulary' | 'verb-phrases'>('vocabulary');
  const [sessionRun, setSessionRun] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);
  const model = useMemo(() => progress ? buildProgressModel(progress) : null, [progress]);
  const analyticsPage = privacyPage
    ? { path: '/personvern', title: 'Personvern' }
    : auth.status === 'loading'
      ? null
      : auth.status === 'authenticated'
        ? {
            path: `/app/${view === 'today' ? 'i-dag' : view === 'vocabulary' ? 'ord' : view === 'verbPhrases' ? 'verbfraser' : 'fremgang'}`,
            title: view === 'today' ? 'I dag' : view === 'vocabulary' ? 'Ord' : view === 'verbPhrases' ? 'Verbfraser' : 'Fremgang'
          }
        : { path: '/', title: 'Forside' };
  useGoogleAnalyticsPageView(analyticsPage?.path ?? null, analyticsPage?.title ?? 'Mellomrom');

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 2600);
  }, []);
  const startSession = useCallback((exercises: Exercise[], kind: 'vocabulary' | 'verb-phrases' = 'vocabulary') => {
    if (!exercises.length) return;
    setSessionKind(kind);
    setQueue(exercises);
    setSessionRun(value => value + 1);
  }, []);
  const closeSession = useCallback(() => {
    setQueue([]);
    if (sessionKind === 'verb-phrases') void verbPhraseCourse.refresh();
    else void vocabularyTraining.refresh();
  }, [sessionKind, verbPhraseCourse.refresh, vocabularyTraining.refresh]);
  const startNextVocabulary = useCallback(async () => {
    const exercises = await vocabularyTraining.refresh();
    if (exercises.length) startSession(exercises);
    else showToast('Ingen nye eller forfalte ord akkurat nå. Du kan repetere fra ordbiblioteket.');
  }, [showToast, startSession, vocabularyTraining.refresh]);
  const practiceWord = useCallback(async (itemId: string) => {
    try {
      const payload = await learningApi.vocabularyPractice(itemId);
      startSession(payload.exercises);
    } catch {
      showToast('Kunne ikke åpne øvelsen for dette ordet.');
    }
  }, [showToast, startSession]);
  const practiceVerbModule = useCallback(async (moduleId: string) => {
    try {
      const payload = await learningApi.verbPhraseTraining(moduleId);
      if (payload.exercises.length) startSession(payload.exercises, 'verb-phrases');
      else showToast('Det er ingen tilgjengelige oppgaver i denne modulen akkurat nå.');
    } catch {
      showToast('Kunne ikke åpne verbmodulen.');
    }
  }, [showToast, startSession]);

  function navigate(next: ViewName) {
    setView(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  if (privacyPage) return <><PrivacyPage /><AnalyticsConsent /></>;
  if (auth.status === 'loading') return <><LoadingScreen /><AnalyticsConsent /></>;
  if (auth.status === 'unauthenticated' || !auth.user) {
    return <><LandingPage registrationMode={auth.registrationMode} googleAuthEnabled={auth.googleAuthEnabled} serviceError={auth.serviceError} onAuthenticate={auth.authenticate} /><AnalyticsConsent /></>;
  }
  const daily = vocabularyTraining.exercises;

  return <div className="app-shell">
    <Sidebar activeView={view} user={auth.user} onNavigate={navigate} onLogout={auth.logout} />
    <main className="main-content">
      {(progressError || vocabularyTraining.error) && <p className="app-notice">{progressError || vocabularyTraining.error}</p>}
      {view === 'today' && <TodayView user={auth.user} progress={progress} model={model} exercises={daily} trainingLoading={vocabularyTraining.loading} dueCount={vocabularyTraining.dueCount} newCount={vocabularyTraining.newCount} onStart={startSession} onLogout={auth.logout} showToast={showToast} />}
      {view === 'vocabulary' && <VocabularyView progressVersion={progress?.todayAttempts ?? 0} onPracticeWord={practiceWord} onStartNext={startNextVocabulary} />}
      {view === 'verbPhrases' && verbPhraseEnabled && <VerbPhraseView course={verbPhraseCourse.course} loading={verbPhraseCourse.loading} error={verbPhraseCourse.error} onStartModule={practiceVerbModule} />}
      {view === 'progress' && <ProgressView progress={progress} model={model} />}
    </main>
    <MobileNav activeView={view} features={auth.user.features} onNavigate={navigate} />
    {queue.length > 0 && <ExerciseSession key={sessionRun} queue={queue} onClose={closeSession} onContinue={startNextVocabulary} onSave={saveAttempt} showToast={showToast} />}
    <Toast message={toast} />
    <AnalyticsConsent />
  </div>;
}
